'use client';

import { useState, useEffect, useMemo, createContext, useContext, ReactNode } from 'react';
import { userAppData, UserAppData } from './UserAppData';
import { useLocale, type Locale, locales } from '@/app/user-and-session/languages/i18n';

import type { UserLocationData } from './UserAppData';

/**
 * Context for UserAppData
 */
interface UserAppDataContextType {
  userAppData: typeof userAppData;
}

const UserAppDataContext = createContext<UserAppDataContextType | undefined>(undefined);

/**
 * Provider for UserAppData context
 */
export function UserAppDataProvider({ children }: { children: ReactNode }) {
  return (
    <UserAppDataContext.Provider value={{ userAppData }}>
      {children}
    </UserAppDataContext.Provider>
  );
}

/**
 * Hook to access UserAppData context
 */
function useUserAppDataContext() {
  const context = useContext(UserAppDataContext);
  if (context === undefined) {
    throw new Error('useUserAppData must be used within a UserAppDataProvider');
  }
  return context;
}

/**
 * Combined state interface for all user app data
 */
export interface UserAppDataState {
  userEmail: string;
  userId: string;
  userPreferredName: string;
  creditCount: number;
  dailyCreditCount: number;
  language: string | null;
  userLocation?: UserLocationData;
  isInitialized: boolean;
}

/**
 * Actions interface for modifying user app data
 */
export interface UserAppDataActions {
  // User ID actions
  setUserId: (userId: string) => void;

  // User preferred name actions
  setUserPreferredName: (preferredName: string) => void;
  loadUserPreferredNameFromDB: () => Promise<void>;
  setUserPreferredNameAndWriteToDB: (preferredName: string) => Promise<boolean>;

  // Language actions
  setLanguage: (language: string | null) => void;
  loadUserLanguageFromDB: () => Promise<void>;
  setLanguageAndWriteToDB: (language: string | null) => Promise<boolean>;

  // Gem count actions
  addGems: (amount: number, dailyAmount?: number) => Promise<{success: boolean, newCount: number, message: string}>;
  subtractGems: (amount: number) => Promise<{success: boolean, newCount: number, message: string}>;

  // Location actions
  setUserLocation: (location: UserLocationData | undefined) => void;
  updateUserLocationFromHeaders: (headers: { get: (key: string) => string | null }) => void;

  // Bulk data actions
  initializeUserDataFromDB: (userEmail: string) => Promise<boolean>;

  // Utility actions
  reset: () => void;
}

/**
 * React hook for accessing and managing user app data
 * Provides automatic re-rendering when state changes
 * Automatically initializes user data from database if not already initialized
 */
export function useUserAppData(userEmail: string): [UserAppDataState, UserAppDataActions] {
  const { userAppData: userAppDataInstance } = useUserAppDataContext();
  const { setLocale } = useLocale();

  // Local state that triggers re-renders (following LiveMessage pattern)
  const [, forceUpdate] = useState({});

  // Subscribe to state changes and auto-initialize if needed
  useEffect(() => {
    const triggerUpdate = () => forceUpdate({});
    const unsubscribe = userAppDataInstance.subscribe(triggerUpdate);

    // Auto-initialize user data from database if not already initialized for this user
    const initializeIfNeeded = async () => {
      // Only initialize if we have a valid, non-empty userEmail
      if (userEmail && userEmail.trim() !== '' && !userAppDataInstance.isInitializedForUser(userEmail)) {
        try {
          await userAppDataInstance.initializeUserDataFromDB(userEmail);
          
          // After initialization, check if language is set and update locale
          const loadedLanguage = userAppDataInstance.getLanguage();
          if (loadedLanguage !== null && loadedLanguage !== undefined) {
            // Validate that the language is a supported locale
            if (locales.includes(loadedLanguage as Locale)) {
              setLocale(loadedLanguage as Locale);
            }
          }
        } catch (error) {
          console.error('Failed to auto-initialize user data:', error);
        }
      }
    };

    initializeIfNeeded();

    return unsubscribe;
  }, [userEmail, userAppDataInstance, setLocale]); // Include userEmail, userAppDataInstance, and setLocale in dependency array
  
  // Get current state data
  const stateData: UserAppDataState = {
    userEmail: userAppDataInstance.getUserEmail(),
    userId: userAppDataInstance.getUserId(),
    userPreferredName: userAppDataInstance.getUserPreferredName(),
    creditCount: userAppDataInstance.getCreditCount(),
    dailyCreditCount: userAppDataInstance.getDailyCreditCount(),
    language: userAppDataInstance.getLanguage(),
    userLocation: userAppDataInstance.getUserLocation(),
    isInitialized: userAppDataInstance.isInitializedForUser(userEmail),
  };
  
  // Actions for modifying state - MEMOIZED to prevent infinite loops
  const actions: UserAppDataActions = useMemo(() => ({
    // User ID actions
    setUserId: (userId: string) => {
        userAppDataInstance.setUserId(userId);
    },

    // User preferred name actions
    setUserPreferredName: (preferredName: string) => {
        userAppDataInstance.setUserPreferredName(preferredName);
    },

    loadUserPreferredNameFromDB: async () => {
        await userAppDataInstance.loadUserPreferredNameFromDB();
    },

    setUserPreferredNameAndWriteToDB: async (preferredName: string) => {
        return await userAppDataInstance.setUserPreferredNameAndWriteToDB(preferredName);
    },

    // Language actions
    setLanguage: (language: string | null) => {
        userAppDataInstance.setLanguage(language);
    },

    loadUserLanguageFromDB: async () => {
        await userAppDataInstance.loadUserLanguageFromDB();
    },

    setLanguageAndWriteToDB: async (language: string | null) => {
        return await userAppDataInstance.setLanguageAndWriteToDB(language);
    },

    // Gem count actions
    addGems: async (amount: number, dailyAmount: number = 0) => {
      return await userAppDataInstance.addCredits(amount, dailyAmount);
    },

    subtractGems: async (amount: number) => {
      return await userAppDataInstance.subtractCredits(amount);
    },

    // Location actions
    setUserLocation: (location: UserLocationData | undefined) => {
      userAppDataInstance.setUserLocation(location);
    },

    updateUserLocationFromHeaders: (headers: { get: (key: string) => string | null }) => {
      userAppDataInstance.updateUserLocationFromHeaders(headers);
    },

    initializeUserDataFromDB: async (userEmail: string) => {
      return await userAppDataInstance.initializeUserDataFromDB(userEmail);
    },

    // Utility actions
    reset: () => {
      userAppDataInstance.reset();
    },
  }), [userAppDataInstance]); // Include userAppDataInstance in dependency array
  
  return [stateData, actions];
}

/**
 * Hook for checking if user data is initialized for a specific user
 */
export function useUserDataInitialized(userEmail: string) {
  const { userAppData: userAppDataInstance } = useUserAppDataContext();

  const [, forceUpdate] = useState({});

  useEffect(() => {
    const triggerUpdate = () => forceUpdate({});
    const unsubscribe = userAppDataInstance.subscribe(triggerUpdate);
    return unsubscribe;
  }, [userAppDataInstance]);

  return {
    isInitialized: userAppDataInstance.isInitializedForUser(userEmail),
  };
}
